import { Fragment, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { INBOUND_APPOINTMENTS } from '../../../../navigation/constants'

const T = 'receiving_config.errors'

export const ConflictsDialog = ({ open, setOpen, conflicts = [], warehouseId }) => {
  const { t } = useTranslation()
  const closeButtonRef = useRef(null)
  // The board (InboundAppointmentsBoardContainer) derives its date from
  // `pickedDate ?? todayInTimezone(warehouse.time_zone)` and reads no query parameter, so a
  // `?date=` here would be an inert deep link that does not exist. Link to the board path only.
  const board = INBOUND_APPOINTMENTS.replace(':warehouse_id', warehouseId)
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={closeButtonRef} onClose={() => setOpen(false)}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                {t(`${T}.conflicts_title`)}
              </Dialog.Title>
              <p className="mt-2 text-sm text-gray-600">{t(`${T}.conflicts_intro`)}</p>
              <table className="mt-3 min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500">
                    <th className="py-1 pr-4">{t(`${T}.conflicts_headers.appointment`)}</th>
                    <th className="py-1 pr-4">{t(`${T}.conflicts_headers.date`)}</th>
                    <th className="py-1 pr-4">{t(`${T}.conflicts_headers.franja`)}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {conflicts.map((c, i) => (
                    // Keyed by appointment_id + index, not appointment_id alone: a payload that
                    // repeats the same appointment_id across rows (e.g. one appointment
                    // conflicting for two reasons) would otherwise collide on key.
                    <tr key={`${c.appointment_id}-${i}`} className="border-t border-gray-100">
                      <td className="py-1 pr-4">#{c.appointment_id}</td>
                      <td className="py-1 pr-4">{c.date}</td>
                      <td className="py-1 pr-4">{c.franja_label}</td>
                      <td className="py-1 text-right">
                        <Link to={board} className="text-indigo-600 hover:underline">
                          {t('receiving_config.warnings.view_board')}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-end pt-4">
                <button
                  type="button"
                  ref={closeButtonRef}
                  className="inline-flex justify-center rounded-md border border-transparent shadow-sm py-2 px-4 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  onClick={() => setOpen(false)}
                >
                  {t(`${T}.conflicts_close`)}
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
